import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useReportIssue } from '@quizzach/api-hooks';
import { Fail, Success } from '@quizzach/assets';
import { BtnTeal, MailTo, TextareaOutlined } from '@quizzach/styles';
import { Select, ModalShell } from '@quizzach/components';

interface IQuizReportIssueModalProps {
	showModal: boolean;
	question_uuid: string;
	closeModal: () => void;
}

export const ReportIssueModal: React.FC<IQuizReportIssueModalProps> = ({
	showModal,
	question_uuid,
	closeModal,
}) => {
	const { t } = useTranslation();
	const [issueText, setIssueText] = useState<string>('');
	const [issueReason, setIssueReason] = useState('');
	const { state, error, reportIssue } = useReportIssue();

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		await reportIssue({ issueText, issueReason, question_uuid: question_uuid });
		setIssueText('');
	};

	const reasons = [
		{
			key: 'incorrect-answer',
			display: t('modal.reportIssue.reasonIncorrectAnswer'),
		},
		{
			key: 'question-repeated',
			display: t('modal.reportIssue.reasonQuestionRepeated'),
		},
		{
			key: 'obsolete-question',
			display: t('modal.reportIssue.reasonObsoleteQuestion'),
		},
		{ key: 'view-issue', display: t('modal.reportIssue.reasonViewIssue') },
		{ key: 'other', display: t('modal.reportIssue.reasonOther') },
	];

	const handleReasonChange = (selectedValue: string) => {
		const selectedReason = reasons.find(
			(reason) => reason.display === selectedValue
		);
		setIssueReason(selectedReason?.key || '');
	};

	if (state === 'submitting') {
		return (
			<ModalShell
				show={showModal}
				onClose={closeModal}
				title={t('modal.reportIssue.title')}
			>
				<Select
					label={t('modal.reportIssue.selectReason')}
					values={reasons.map((reason) => reason.display)}
					onChange={handleReasonChange}
				/>
				<form onSubmit={handleSubmit}>
					<TextareaOutlined
						value={issueText}
						onChange={(e) => setIssueText(e.target.value)}
						placeholder={t('modal.reportIssue.placeholder')}
						rows={8}
						cols={60}
						style={{ width: '100%' }}
					/>
					<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
						<BtnTeal
							$small
							type='submit'
							disabled={issueReason.length <= 0}
							style={{ marginTop: '16px' }}
						>
							{t('modal.reportIssue.submit')}
						</BtnTeal>
					</div>
				</form>
			</ModalShell>
		);
	}

	return (
		<ModalShell
			show={showModal}
			onClose={closeModal}
			size='sm'
			center
			icon={!error ? <Success size={40} /> : <Fail size={40} />}
			title={
				!error
					? t('modal.reportIssue.successTitle')
					: t('modal.reportIssue.failTitle')
			}
		>
			{!error ? (
				<p style={{ margin: 0 }}>{t('modal.reportIssue.successText')}</p>
			) : (
				<p style={{ margin: 0 }}>
					{t('modal.reportIssue.failText')}{' '}
					<MailTo href='mailto:team@quizzach.com'>team@quizzach.com</MailTo>.
				</p>
			)}
		</ModalShell>
	);
};
